import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Initialize user from localStorage to prevent flash
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(!localStorage.getItem('user')); // Only loading if no cached user
  // `bootstrapped` becomes true after the initial /auth/me check finishes (or skipped).
  // Routes should only show the splash spinner during bootstrap — NOT during in-flight login/register
  // operations (which would otherwise unmount AuthPage and wipe its local state like `error` / `shake`).
  const [bootstrapped, setBootstrapped] = useState(!!localStorage.getItem('user'));
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  // Fetch user data on mount if we have a token
  useEffect(() => {
    isMounted.current = true;
    
    const loadUser = async () => {
      // CRITICAL: If returning from Google OAuth callback, skip the /me check —
      // AuthCallback will exchange the session_id and establish the session first.
      if (window.location.hash?.includes('session_id=')) {
        setLoading(false);
        setBootstrapped(true);
        return;
      }
      if (!token) {
        setLoading(false);
        setBootstrapped(true);
        return;
      }

      try {
        const response = await api.get('/auth/me');
        if (isMounted.current) {
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching user:', err);
        if (isMounted.current) {
          // Only clear on explicit 401, not on network errors
          if (err.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
          // On network error, keep the cached user
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
          setBootstrapped(true);
        }
      }
    };

    loadUser();
    
    return () => {
      isMounted.current = false;
    };
  }, [token]);

  const login = async (email, password) => {
    setError(null);
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });

      // 2FA challenge — return a flag so caller can prompt for the code
      if (response.data.requires_2fa) {
        return { requires_2fa: true, temp_token: response.data.temp_token };
      }

      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setToken(access_token);
      setUser(userData);
      return userData;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Error al iniciar sesión. Intenta de nuevo.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const verify2FALogin = async (temp_token, code) => {
    setError(null);
    setLoading(true);
    try {
      const response = await api.post('/auth/2fa/verify-login', { temp_token, code });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setToken(access_token);
      setUser(userData);
      return userData;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Código 2FA inválido';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Emergent-managed Google sign-in: exchange session_id (backend-side) for app JWT
  const loginWithGoogleSession = async (sessionId) => {
    setError(null);
    const response = await api.post('/auth/google/session', { session_id: sessionId });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    setLoading(false);
    setBootstrapped(true);
    return userData;
  };

  const register = async (email, password, business_name, currency, language) => {
    setError(null);
    setLoading(true);
    try {
      const response = await api.post('/auth/register', {
        email,
        password,
        business_name,
        currency,
        language,
      });
      const { access_token, user: userData } = response.data;
      
      // Save to localStorage
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setToken(access_token);
      setUser(userData);
      return userData;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Error al registrar. Intenta de nuevo.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(() => {
    // Best-effort call to clear the httpOnly cookie on the server
    api.post('/auth/logout').catch(() => { /* ignore */ });
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setLoading(false);
    setError(null);
  }, []);

  const updateSettings = async (settings) => {
    try {
      const params = new URLSearchParams();
      Object.entries(settings).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value);
        }
      });
      
      await api.put(`/auth/settings?${params.toString()}`);
      const updatedUser = { ...user, ...settings };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return { success: true };
    } catch (err) {
      console.error('Error updating settings:', err);
      return { success: false, error: err.response?.data?.detail || 'Error al guardar configuración' };
    }
  };

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (err) {
      console.error('Error refreshing user:', err);
    }
  }, [token]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading,
      bootstrapped,
      error,
      login, 
      verify2FALogin,
      loginWithGoogleSession,
      register, 
      logout, 
      updateSettings,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
